<template>
  <div v-if="notify">
    <div id="notification-box" class="notification-box">
      <button v-if="data && data.type == 'call'" id="notification-close" @click="RejectCall(data.videoConsultationId)"
        title="close" aria-label="Close">
        ✕
      </button>
      <button v-else id="notification-close" @click="closed" title="close" aria-label="Close">✕</button>

      <div class="notification-data w-full">
        <div class="cursor-pointer flex justify-start items-center mt-3">
          <img class="rounded-lg w-16 h-16 object-cover" :src="userimg
            ? userimg
            : 'https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png'
            " />
          <div class="ml-3">
            <h3 class="text-primary-second capitalize mb-1 font-semibold">
              {{ title }}
            </h3>
            <h4 class="text-primary-second capitalize mb-1">
              {{ content }}
            </h4>
          </div>
        </div>
        <div class="flex gap-2 items-center justify-center mt-2 mb-2" v-if="isCall">
          <button @click="AcceptCall(data.videoConsultationId)"
            class="text-sm px-4 py-2 rounded-full text-white bg-primary">
            Accept
          </button>
          <button @click="RejectCall(data.videoConsultationId)"
            class="text-sm px-4 py-2 rounded-full text-white bg-danger">
            Reject
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import emitter from "@/event-bus";
import { handleFcm } from "@/utils/alertToasts";

export default {
  name: "IncommingNotification",

  data() {
    return {
      title: "",
      content: "",
      userimg: "",
      data: null,
      notify: false,
      isCall: false,
      _checkInterval: null
    };
  },

  created() {
    this.receiveMessage();
  },

  beforeDestroy() {
    this.stopStatusPolling();
  },

  methods: {
    closed() {
      this.notify = false;
    },

    RejectCall(consultationId) {
      this.$http
        .patch(
          process.env.VUE_APP_TRANSACTIONS_SERVICE_API +
            `video-consultations/${consultationId}?status=rejected`,
          {},
          {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("accessToken")
            }
          }
        )
        .then((res) => {
          this.stopStatusPolling();
          this.closed();
          this.displaySuccess("Call Rejected");
        })
        .catch((err) => {
          this.displayError(err.response.data.message);
        });
    },

    AcceptCall(consultationId) {
      this.$http
        .patch(
          process.env.VUE_APP_TRANSACTIONS_SERVICE_API +
            `video-consultations/${consultationId}/accept`,
          {},
          {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("accessToken")
            }
          }
        )
        .then((res) => {
          // The accept endpoint normally returns the OpenTok credentials. Keep
          // the response shape tolerant because some deployments wrap them in
          // `session` while others return them directly under `data`.
          const session = res?.data?.data?.session || res?.data?.data;
          if (!session?.sessionId || !session?.token) {
            throw new Error('The call session credentials were not returned by the server.');
          }
          session["videoConsultationId"] = consultationId;
          localStorage.setItem("_openTokSession", JSON.stringify(session));
          this.stopStatusPolling();
          this.closed();
          this.displaySuccess(res.data.message);
          this.$router.push("/call");
        })
        .catch((err) => {
          this.displayError(err.response.data.message);
        });
    },

    receiveMessage() {
      try {
        // Listen to foreground FCM messages forwarded by firebase.js
        emitter.on("fcm-message", (payload) => {
          try {
            const data = payload && payload.data ? payload.data : {};
            // TB-191: a toast already showed this notification: no second card
            if (!(data && data.type === "call") && handleFcm(payload)) return;
            this.notify = true;
            if (data) localStorage.setItem("caller", JSON.stringify(data));
            this.isCall = data && data.type === "call" ? true : false;
            this.title = payload?.notification?.title || (this.isCall ? "Incoming Call" : "Notification");
            this.content = this.isCall ? `from Dr.${data.name || ""}` : (payload?.notification?.body || "");
            this.userimg = data.image || "";
            this.data = data;

            // If this is a call, start polling consultation status so we can auto-close if caller cancels
            if (!this.isCall) {
              setTimeout(() => {
                this.closed();
              }, 5000);
            } else {
              try {
                this.startStatusPolling(data.videoConsultationId);
              } catch (_) {}
            }
          } catch (innerErr) {
            // swallow UI errors
          }
        });
      } catch (e) {
        // swallow
      }
    },

    startStatusPolling(consultationId) {
      try {
        this.stopStatusPolling();
      } catch (_) {}
      if (!consultationId) return;
      this._checkInterval = setInterval(async () => {
        try {
          const res = await this.$http.get(`${process.env.VUE_APP_TRANSACTIONS_SERVICE_API}video-consultations/${consultationId}`, { headers: { Authorization: 'Bearer ' + localStorage.getItem('accessToken') } });
          const status = (res?.data?.data?.status || res?.data?.status || '').toString().toLowerCase();
          if (!status) return;
          if (['cancelled', 'no_answer', 'rejected'].includes(status)) {
            try { this.stopStatusPolling() } catch (_) {}
            try { this.closed() } catch (_) {}
          }
          if (status === 'accepted') {
            // If accepted remotely, close the popup (AcceptCall flow will navigate)
            try { this.stopStatusPolling() } catch (_) {}
            try { this.closed() } catch (_) {}
          }
        } catch (_) {}
      }, 2000);
    },

    stopStatusPolling() {
      try {
        if (this._checkInterval) clearInterval(this._checkInterval);
      } catch (_) {}
      this._checkInterval = null;
    }
  },

  validations: function () {
    return {};
  },

  computed: {}
};
</script>

<style>
.notification-box {
  display: flex;
  position: fixed; /* fixed to viewport */
  right: 20px;
  bottom: 20px;
  width: 370px;
  max-width: 90%;
  height: auto;
  background-color: var(--tb-bg-elevated);
  z-index: 99999;
  box-shadow: var(--tb-shadow-lg);
  border-radius: 8px;
}

#notification-close {
  position: absolute;
  color: var(--tb-text-muted);
  font: 14px;
  right: 5px;
  text-decoration: none;
  text-shadow: 0 1px 0 var(--tb-bg-elevated);
  top: 2px;
  z-index: 10;
  cursor: pointer;
}

.notification-data {
  margin: 0 20px;
}

.notification-data p {
  margin: 10px 0;
}

.notification-notice p {
  font-size: 14px;
  font-weight: 400;
}

.notification-title p {
  font-size: 18px;
  font-weight: bold;
  line-height: 18px;
}

.notification-time small {
  font-size: 12px;
}

.notification-wall-number small {
  font-size: 8px;
}
</style>
